import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import ThesisGallery from './components/ThesisGallery';
import ThesisDetailView from './components/ThesisDetailView';
import ThesisUpload from './components/ThesisUpload';
import AdminDashboard from './components/AdminDashboard';
import ThesisList from './components/ThesisList';
import Login from './components/Login';
import UserManagement from './components/UserManagement';
import UserProfile from './components/UserProfile';
import PlagiarismChecker from './components/PlagiarismChecker';
import Home from './components/Home';

const SCHOOL_LOGO_PATH = '/logo.png';

function App() {
    const [user, setUser] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [backendReady, setBackendReady] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const menuRef = useRef(null);
    const backendWasDownRef = useRef(false);
    const hasEverBeenReadyRef = useRef(false);
    const backendDownAtRef = useRef(0);
    const backendEverDownRef = useRef(false);

    useEffect(() => {
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
            setUser(JSON.parse(savedUser));
        }
        setIsInitialized(true);
    }, []);

    useEffect(() => {
        if (backendReady) {
            if (!hasEverBeenReadyRef.current) {
                hasEverBeenReadyRef.current = true;
            }

            if (backendWasDownRef.current || backendEverDownRef.current) {
                backendWasDownRef.current = false;
                const key = 'backend_auto_reload_at';
                const now = Date.now();
                const last = Number(sessionStorage.getItem(key) || 0);
                if (!last || now - last > 3000) {
                    sessionStorage.setItem(key, String(now));
                    setRefreshKey((v) => v + 1);
                }
            }
            return;
        }

        backendEverDownRef.current = true;
        if (hasEverBeenReadyRef.current) {
            backendWasDownRef.current = true;
        }
        if (!backendDownAtRef.current) {
            backendDownAtRef.current = Date.now();
        }
    }, [backendReady]);

    useEffect(() => {
        let active = true;
        let timerId = null;
        let intervalId = null;
        let inFlight = false;

        const checkBackend = async () => {
            if (!active || inFlight) return;
            inFlight = true;
            try {
                await axios.get('/api/health', { timeout: 3000 });
                if (active) setBackendReady(true);
            } catch (err) {
                if (!active) return;
                setBackendReady(false);
                timerId = setTimeout(checkBackend, 1500);
            } finally {
                inFlight = false;
            }
        };

        checkBackend();
        intervalId = setInterval(checkBackend, 5000);

        const interceptorId = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                const isNetworkError = !error.response;
                const isTimeout = error.code === 'ECONNABORTED';
                if (isNetworkError || isTimeout) {
                    if (active) setBackendReady(false);
                }
                return Promise.reject(error);
            }
        );

        return () => {
            active = false;
            if (timerId) clearTimeout(timerId);
            if (intervalId) clearInterval(intervalId);
            axios.interceptors.response.eject(interceptorId);
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!isInitialized) {
        return (
            <div className="loading-overlay">
                <div className="loading-card">
                    <div className="spinner" />
                    <h3>Starting up...</h3>
                    <p className="muted">Loading your session</p>
                </div>
            </div>
        );
    }

    const handleLogout = () => {
        localStorage.removeItem('user');
        setUser(null);
    };

    return (
        <Router>
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    .no-print, .no-print * { display: none !important; }
                }
                .no-copy {
                    user-select: none;
                    -webkit-user-select: none;
                }
            `}</style>

            {!backendReady && (
                <div className="loading-overlay">
                    <div className="loading-card">
                        <div className="spinner" />
                        <h3>Connecting to the server...</h3>
                        <p className="muted">Please wait while the backend wakes up</p>
                    </div>
                </div>
            )}

            <nav className="nav">
                <div className="nav-inner">
                    <div className="nav-links">
                        <Link to="/" className="nav-brand" aria-label="NVC Thesis Archive home">
                            <img
                                src={SCHOOL_LOGO_PATH}
                                alt="NVC logo"
                                className="nav-brand-logo"
                            />
                            <span>NVC Thesis Repository</span>
                        </Link>
                        <Link to="/" className="nav-link">Home</Link>
                        <Link to="/gallery" className="nav-link">Browse</Link>
                        {user && (
                            <Link to="/upload" className="nav-link">Upload Thesis</Link>
                        )}
                    </div>

                    <div className="nav-actions">
                        {user ? (
                            <div className="profile-menu" ref={menuRef}>
                                <button
                                    onClick={() => setMenuOpen((open) => !open)}
                                    className="profile-trigger"
                                    aria-expanded={menuOpen}
                                    aria-haspopup="true"
                                >
                                    <span className="profile-bars" aria-hidden="true">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </span>
                                    <span className="profile-label">Profile</span>
                                </button>
                                {menuOpen && (
                                    <div className="profile-dropdown">
                                        <div className="profile-header">
                                            <div className="profile-name">{user.full_name}</div>
                                            <div className="profile-email">{user.email || 'No email on file'}</div>
                                            <div className="profile-role">Role: {user.role}</div>
                                        </div>
                                        <Link to="/profile" className="profile-item" onClick={() => setMenuOpen(false)}>Profile Settings</Link>
                                        {user.role === 'admin' && (
                                            <Link to="/admin" className="profile-item" onClick={() => setMenuOpen(false)}>Admin Dashboard</Link>
                                        )}
                                        {(user.role === 'staff' || user.role === 'admin') && (
                                            <Link to="/list" className="profile-item" onClick={() => setMenuOpen(false)}>Manage Thesis Projects</Link>
                                        )}
                                        {user.role === 'admin' && (
                                            <>
                                                <Link to="/users" className="profile-item" onClick={() => setMenuOpen(false)}>Manage Users</Link>
                                                <Link to="/checker" className="profile-item" onClick={() => setMenuOpen(false)}>Similarity Checker</Link>
                                            </>
                                        )}
                                        <button onClick={handleLogout} className="profile-item profile-logout">Logout</button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Link to="/login" className="btn btn-primary btn-small">Staff/Admin Login</Link>
                        )}
                    </div>
                </div>
            </nav>

            <div className="app-shell">
                <Routes key={refreshKey}>
                    {/* Public Routes */}
                    <Route path="/" element={<Home user={user} />} />
                    <Route path="/gallery" element={<ThesisGallery currentUser={user} />} />
                    <Route path="/gallery/:department/:major/:year" element={<ThesisDetailView currentUser={user} />} />
                    <Route path="/gallery/:department/:year" element={<ThesisDetailView currentUser={user} />} />
                    <Route path="/login" element={<Login setUser={setUser} />} />

                    {/* Authenticated Routes */}
                    <Route path="/profile" element={user ? <UserProfile currentUser={user} setUser={setUser} /> : <Navigate to="/login" />} />

                    {/* Staff/Admin Routes */}
                    <Route 
                        path="/upload" 
                        element={user ? <ThesisUpload currentUser={user} /> : <Navigate to="/gallery" />} 
                    />
                    <Route 
                        path="/list" 
                        element={user ? <ThesisList currentUser={user} /> : <Navigate to="/gallery" />} 
                    />
                    
                    {/* Admin Only Routes */}
                    <Route path="/admin" element={user?.role === 'admin' ? <AdminDashboard currentUser={user} /> : <Navigate to="/gallery" />} />
                    <Route path="/users" element={user?.role === 'admin' ? <UserManagement currentUser={user} /> : <Navigate to="/gallery" />} />
                    <Route path="/checker" element={user?.role === 'admin' ? <PlagiarismChecker user={user} /> : <Navigate to="/gallery" />} />

                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            </div>
        </Router>
    );
}

export default App;
